import React from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useIsTeacher } from '@reducers/user/hooks';
import LearnosityExamsEventEmitter, {
  LearnosityExamsEvents,
  MessageFnType,
  TestletSessionCompletedNotificationPayload,
  TestletSessionStateUpdateNotificationPayload,
  TestletSessionTimeExtendedNotificationPayload,
} from '../utils/LearnosityExamsEventEmitter';
import {
  StudentTestletSessionStatus,
  Testlet,
  TestletSession,
  TestletStatus,
} from '../types';
import Message from '../Components/Message';
import {
  updateCachedTestlet,
  updateCachedTestletSession,
  updateTestletInLearnosityExamTestSessions,
} from '../utils/queryHelper';

const useLearnosityExamEventListeners = () => {
  const { courseId } = useParams();
  const isTeacher = useIsTeacher();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const unlockTestletSubscriber: MessageFnType = (payload) => {
      updateCachedTestlet(
        queryClient,
        {
          id: payload.testlet_id,
          name: payload.testlet_name,
          status: TestletStatus.UNLOCKED,
          lock_strategy: payload.lock_strategy,
        } as Testlet,
        payload.test_session_id,
      );
      updateTestletInLearnosityExamTestSessions(
        queryClient,
        {
          id: payload.testlet_id,
          name: payload.testlet_name,
          status: TestletStatus.UNLOCKED,
          lock_strategy: payload.lock_strategy,
        } as Testlet,
        payload.test_session_id,
        payload.section_id,
      );
      const testletSessions = queryClient.getQueryData([
        'testletSessions',
        +payload.testlet_id,
      ]) as TestletSession[];

      if (
        !isTeacher &&
        testletSessions?.[0]?.status !== StudentTestletSessionStatus.COMPLETED
      ) {
        toast.info(
          <Message
            testletId={payload.testlet_id}
            sectionId={payload.section_id}
            courseId={courseId}
            testSessionId={payload.test_session_id}
            message={`${payload.testlet_name} Testlet unlocked`}
            buttonText="Go to the testlet"
          />,
          {
            autoClose: false,
          },
        );
      } else {
        return;
      }
      toast.info(
        <Message
          testletId={payload.testlet_id}
          sectionId={payload.section_id}
          courseId={courseId}
          testSessionId={payload.test_session_id}
          message={`${payload.testlet_name} Testlet unlocked`}
          buttonText={!isTeacher ? 'Go to the testlet' : ''}
        />,
        {
          autoClose: 5000,
        },
      );
    };
    const lockTestletSubscriber: MessageFnType = (payload) => {
      updateCachedTestlet(
        queryClient,
        {
          id: payload.testlet_id,
          name: payload.testlet_name,
          status: TestletStatus.LOCKED,
          lock_strategy: payload.lock_strategy,
        } as Testlet,
        payload.test_session_id,
      );
      updateTestletInLearnosityExamTestSessions(
        queryClient,
        {
          id: payload.testlet_id,
          name: payload.testlet_name,
          status: TestletStatus.LOCKED,
          lock_strategy: payload.lock_strategy,
        } as Testlet,
        payload.test_session_id,
        payload.section_id,
      );
      toast.info(
        <Message
          testletId={payload.testlet_id}
          sectionId={payload.section_id}
          courseId={courseId}
          testSessionId={payload.test_session_id}
          message={`${payload.testlet_name} Testlet locked`}
        />,
        {
          autoClose: 5000,
        },
      );
    };
    const updateTestletSessionCompletedSubscriber: MessageFnType = (message) => {
      const completedMessage = message as TestletSessionCompletedNotificationPayload;
      // trigger to get new scaled scores
      queryClient.invalidateQueries([
        'getExamScaledScore',
        completedMessage.test_session_id,
      ]);
      updateCachedTestletSession(
        queryClient,
        {
          id: completedMessage.testlet_session_id,
          status: completedMessage.status,
          completed_at: completedMessage.completed_at,
          started_at: completedMessage.started_at,
          score: completedMessage.score,
          max_score: completedMessage.max_score,
          active: undefined,
        },
        completedMessage.testlet_id,
      );
      queryClient.invalidateQueries([
        'testSessionCompletionStatus',
        completedMessage.section_id,
      ]);
    };
    const updateTestletSessionSubscriber: MessageFnType = (message) => {
      const updateMessage = message as TestletSessionStateUpdateNotificationPayload;
      updateCachedTestletSession(
        queryClient,
        {
          id: updateMessage.testlet_session_id,
          status: updateMessage.status,
          user_id: updateMessage.user_id,
        },
        updateMessage.testlet_id,
      );
    };
    const extendedTestletSessionTimeSubscriber: MessageFnType = (message) => {
      const extendedMessage = message as TestletSessionTimeExtendedNotificationPayload;
      updateCachedTestletSession(
        queryClient,
        {
          id: extendedMessage.testlet_session_id,
          ...extendedMessage,
        },
        extendedMessage.testlet_id,
      );
      queryClient.invalidateQueries([
        'testSessionCompletionStatus',
        extendedMessage.section_id,
      ]);
    };

    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_UNLOCKED,
      unlockTestletSubscriber,
    );
    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_LOCKED,
      lockTestletSubscriber,
    );

    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_SESSION_IN_PROGRESS,
      updateTestletSessionSubscriber,
    );
    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_SESSION_PAUSED,
      updateTestletSessionSubscriber,
    );
    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_SESSION_COMPLETED,
      updateTestletSessionCompletedSubscriber,
    );
    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.TESTLET_SESSION_TIME_EXTENDED,
      extendedTestletSessionTimeSubscriber,
    );

    return () => {
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_UNLOCKED,
        unlockTestletSubscriber,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_LOCKED,
        lockTestletSubscriber,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_SESSION_IN_PROGRESS,
        updateTestletSessionSubscriber,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_SESSION_PAUSED,
        updateTestletSessionSubscriber,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_SESSION_COMPLETED,
        updateTestletSessionCompletedSubscriber,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.TESTLET_SESSION_TIME_EXTENDED,
        extendedTestletSessionTimeSubscriber,
      );
    };
  }, [courseId, isTeacher, queryClient]);
};

export default useLearnosityExamEventListeners;
